import {
  getFeedbackSummary, getFeedbackMonthly, getFeedbackTopics, getFeedbackBreakdown, getFeedbackRecords,
} from '../../../services/performanceFeedbackAnalyticsService';
import { EMPTY_FILTERS, toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';

// PM analitik P6 — E13 Sürekli geri bildirim analitiği veri katmanı. F0 süzgeçleri (dönem dahil) pmAnalyticsFilters'ın URL
// durumundadır; bu ekrana özgü süzgeçler (tarih aralığı, konu, kaynak tipi, paylaşım düzeyi) ve sekme/kırılım/kayıt türü aynı
// sorgu dizesinde kendi anahtarlarıyla tutulur.

export const CATEGORIES = ['strength', 'development_area'];
export const BADGES = ['teamwork', 'innovation', 'customer_focus', 'excellence', 'mentorship', 'ownership'];
export const SOURCE_TYPES = ['feedback', 'requested', 'recognition'];
export const SHARE_LEVELS = ['named', 'anonymous', 'public', 'private'];
export const RECORD_TYPES = ['feedback', 'recognition', 'request'];
export const DIMENSIONS = ['department', 'manager'];
export const TABS = ['summary', 'records'];
export const UNIT_ROWS = 8;

const Q = {
  from: 'dg', to: 'dl', topics: 'tp', sources: 'src', shares: 'sh', tab: 'tab', recordType: 'rt', dimension: 'dim',
};
const list = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');

export const EMPTY_FEEDBACK = Object.freeze({
  from: null, to: null, topics: [], sources: [], shares: [], tab: 'summary', recordType: 'feedback', dimension: 'department',
});

export function parseFeedbackState(search) {
  const p = new URLSearchParams(search || '');
  const from = p.get(Q.from);
  const to = p.get(Q.to);
  const tab = p.get(Q.tab);
  const rt = p.get(Q.recordType);
  const dim = p.get(Q.dimension);
  return {
    from: isDate(from) ? from : null,
    to: isDate(to) ? to : null,
    topics: list(p.get(Q.topics)).filter((x) => CATEGORIES.includes(x) || BADGES.includes(x)),
    sources: list(p.get(Q.sources)).filter((x) => SOURCE_TYPES.includes(x)),
    shares: list(p.get(Q.shares)).filter((x) => SHARE_LEVELS.includes(x)),
    tab: TABS.includes(tab) ? tab : 'summary',
    recordType: RECORD_TYPES.includes(rt) ? rt : 'feedback',
    dimension: DIMENSIONS.includes(dim) ? dim : 'department',
  };
}

export function serializeFeedbackState(state, baseSearch = '') {
  const p = new URLSearchParams(baseSearch || '');
  Object.values(Q).forEach((k) => p.delete(k));
  const s = { ...EMPTY_FEEDBACK, ...state };
  if (s.from) p.set(Q.from, s.from);
  if (s.to) p.set(Q.to, s.to);
  if (s.topics.length) p.set(Q.topics, s.topics.join(','));
  if (s.sources.length) p.set(Q.sources, s.sources.join(','));
  if (s.shares.length) p.set(Q.shares, s.shares.join(','));
  if (s.tab !== 'summary') p.set(Q.tab, s.tab);
  if (s.recordType !== 'feedback') p.set(Q.recordType, s.recordType);
  if (s.dimension !== 'department') p.set(Q.dimension, s.dimension);
  const out = p.toString();
  return out ? `?${out}` : '';
}

/** Uç parametreleri: F0 (katılımcı durumu ve karşılaştırma hariç) + tarih aralığı + konu/kaynak/paylaşım. */
export function feedbackParams(filters, state) {
  const params = toAnalyticsParams({ ...EMPTY_FILTERS, ...filters, participantStatuses: [], comparePeriodId: null });
  if (state.from) params['date[gte]'] = state.from;
  if (state.to) params['date[lte]'] = state.to;
  if (state.topics?.length) params['topic[in]'] = state.topics.join(',');
  if (state.sources?.length) params['sourceType[in]'] = state.sources.join(',');
  if (state.shares?.length) params['shareLevel[in]'] = state.shares.join(',');
  return params;
}

export class PanelError extends Error {}

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function unwrap(promise) {
  const res = await promise;
  const data = payload(res);
  if (data === undefined) throw new PanelError(res?.data?.message || 'error');
  return data;
}

export const fetchSummary = (params) => unwrap(getFeedbackSummary(params));
export const fetchMonthly = (params) => unwrap(getFeedbackMonthly(params));
export const fetchTopics = (params) => unwrap(getFeedbackTopics(params));
export const fetchBreakdown = (params, dimension) => unwrap(getFeedbackBreakdown({ ...params, dimension }));

/** Kayıt listesi: { ok, data } ya da { teamViewDisabled: true } — "kapalı ayar" ile "yüklenemedi" ayrı durumlardır. */
export async function fetchRecords(params, recordType, pageNumber, pageSize, teamViewDisabledKey) {
  const res = await getFeedbackRecords({ ...params, recordType, pageNumber, pageSize });
  if (res?.data?.status) return { ok: true, data: res.data.data };
  if (res?.data?.message === teamViewDisabledKey) return { ok: false, teamViewDisabled: true };
  return { ok: false };
}

export const hasNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
export const fmt = (v, lang, digits = 2) => (hasNum(v) ? new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)) : '');

/** Metrik değeri metni: gizli / 1:1 kapalı / veri yok / değer (yüzde ya da sayı). */
export function metricText(m, t, lang) {
  if (!m) return '';
  if (m.suppressionReason) return t('performance.feedbackAnalytics.suppressed');
  if (m.unavailableReason) return t(`performance.feedbackAnalytics.reason.${m.unavailableReason}`, { defaultValue: t('performance.operations.kpi.noData') });
  if (!hasNum(m.value)) return t('performance.operations.kpi.noData');
  return m.unit === 'percent' ? `${fmt(m.value, lang)}%` : fmt(m.value, lang, 0);
}

/** Aylık seri satırları (grafik + tablo aynı veriyi kullanır). */
export const monthlyRows = (monthly) => (monthly?.months || []).map((m) => ({
  key: m.month,
  month: m.month,
  feedback: m.feedback,
  recognition: m.recognition,
  requests: m.requests,
  oneOnOneCompleted: m.oneOnOneCompleted,
  oneOnOneDue: m.oneOnOneDue,
}));

export function insightParams(insight, lang) {
  const out = {};
  Object.entries(insight?.params || {}).forEach(([k, v]) => {
    out[k] = typeof v === 'number' ? fmt(v, lang) : (v ?? '');
  });
  return out;
}
